import { baseApi } from './base';
import type {
  Contact,
  ContactListItem,
  CreateContactRequest,
  ContactGroup,
  ContactGroupList,
  CreateGroupRequest,
  Tag,
  TagList,
  CreateTagRequest,
} from '@/types/contact';

export const contactService = {
  async getContacts(params: { limit: number; offset: number; ordering: string; search: string; groups?: string[]; tags?: string[]; }): Promise<{ results: ContactListItem[], count: number }> {
    const queryParams: Record<string, string> = {
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
      search: params.search,
    };

    if (params.groups && params.groups.length > 0) {
      queryParams.groups = params.groups.join(',');
    }

    if (params.tags && params.tags.length > 0) {
      queryParams.tags = params.tags.join(',');
    }

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: ContactListItem[], count: number }>(`/contacts/?${query}`);
  },

  async createContact(data: CreateContactRequest): Promise<Contact> {
    return baseApi.request<Contact>('/contacts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getContact(id: string): Promise<Contact> {
    return baseApi.request<Contact>(`/contacts/${id}/`);
  },

  async updateContact(id: string, data: Partial<CreateContactRequest>): Promise<Contact> {
    return baseApi.request<Contact>(`/contacts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteContact(id: string): Promise<void> {
    return baseApi.request<void>(`/contacts/${id}/`, {
      method: 'DELETE',
    });
  },

  async getGroups(params: { limit: number; offset: number; ordering: string; search?: string; }): Promise<{ results: ContactGroupList[], count: number }> {
    const query = new URLSearchParams({
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
      ...(params.search ? { search: params.search } : {}),
    }).toString();
    return baseApi.request<{ results: ContactGroupList[], count: number }>(`/groups/?${query}`);
  },

  async createGroup(data: CreateGroupRequest): Promise<ContactGroup> {
    return baseApi.request<ContactGroup>('/groups/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getGroup(id: string): Promise<ContactGroupList> {
    return baseApi.request<ContactGroupList>(`/groups/${id}/`);
  },

  async updateGroup(id: string, data: Partial<CreateGroupRequest>): Promise<ContactGroup> {
    return baseApi.request<ContactGroup>(`/groups/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteGroup(id: string): Promise<void> {
    return baseApi.request<void>(`/groups/${id}/`, {
      method: 'DELETE',
    });
  },

  async getTags(params: { limit: number; offset: number; ordering: string; search?: string; }): Promise<{ results: TagList[], count: number }> {
    const query = new URLSearchParams({
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
      ...(params.search ? { search: params.search } : {}),
    }).toString();
    return baseApi.request<{ results: TagList[], count: number }>(`/tags/?${query}`);
  },

  async createTag(data: CreateTagRequest): Promise<Tag> {
    return baseApi.request<Tag>('/tags/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getTag(id: string): Promise<TagList> {
    return baseApi.request<TagList>(`/tags/${id}/`);
  },

  async updateTag(id: string, data: Partial<CreateTagRequest>): Promise<Tag> {
    return baseApi.request<Tag>(`/tags/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteTag(id: string): Promise<void> {
    return baseApi.request<void>(`/tags/${id}/`, {
      method: 'DELETE',
    });
  },
};
